import { useEffect, useRef, useState } from 'react';

export type Moment = 'done' | 'live' | 'unblocked';

/**
 * A short-lived moment when a value changes while on screen, never on first render,
 * so a page that loads already-done work stays still.
 */
export function useChangeMoment<T>(
  value: T,
  pick: (previous: T, next: T) => Moment | null,
  duration = 1100,
  memoryKey?: string,
): Moment | null {
  const previous = useRef<T>(recall(memoryKey, value));
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const [moment, setMoment] = useState<Moment | null>(null);
  useEffect(() => {
    remember(memoryKey, value);
    if (Object.is(previous.current, value)) return;
    const next = pickRef.current(previous.current, value);
    previous.current = value;
    if (!next) return;
    setMoment(next);
    const timer = window.setTimeout(() => setMoment(null), duration);
    return () => window.clearTimeout(timer);
  }, [value, duration, memoryKey]);
  useEffect(() => () => remember(memoryKey, previous.current), [memoryKey]);
  return moment;
}

const RECALL_MS = 3000;
const recent = new Map<string, { value: unknown; at: number }>();

/** A card dropped in another column mounts afresh; this lets it still see what it was a moment ago. */
function recall<T>(key: string | undefined, fallback: T): T {
  if (!key) return fallback;
  const entry = recent.get(key);
  return entry && Date.now() - entry.at < RECALL_MS ? (entry.value as T) : fallback;
}

function remember(key: string | undefined, value: unknown): void {
  if (key) recent.set(key, { value, at: Date.now() });
}

/** Done or Live arriving, or a Waiting item being let go. */
export function statusMoment(previous: string, next: string): Moment | null {
  if (next === 'live') return 'live';
  if (next === 'done') return 'done';
  if (previous === 'waiting') return 'unblocked';
  return null;
}
